import { useMemo } from 'react';
import { Alert, Button, Empty, Space, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FleetVehicle, PageMeta } from '@goelectric/shared-types';
import { FLEET_INACTIVE_HINT } from '../utils/fleetMessages';
import { FleetAuthorizationBadge, FleetVehicleStatusBadge } from './FleetBadges';

export interface FleetVehiclesTableProps {
  vehicles: FleetVehicle[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  canWrite: boolean;
  /** La flota está inactiva: no se ofrece reactivar (el backend lo rechazaría). */
  fleetInactive?: boolean;
  onEdit: (vehicle: FleetVehicle) => void;
  onDeactivate: (vehicle: FleetVehicle) => void;
  onReactivate: (vehicle: FleetVehicle) => void;
  onRetry: () => void;
  onOffsetChange: (offset: number) => void;
}

/**
 * Tabla de vehículos de la flota (US-2.3, ADR 0047). Muestra el UID RFID físico (8/14
 * hex), el estado del vehículo y el estado de sincronización del token en los tenants de
 * CitrineOS. Inactivar es una baja lógica (DELETE) que bloquea el RFID.
 */
export function FleetVehiclesTable({
  vehicles,
  meta,
  loading,
  error,
  canWrite,
  fleetInactive = false,
  onEdit,
  onDeactivate,
  onReactivate,
  onRetry,
  onOffsetChange,
}: FleetVehiclesTableProps) {
  const columns = useMemo<ColumnsType<FleetVehicle>>(
    () => [
      {
        title: 'Vehículo',
        key: 'vehicle',
        fixed: 'left',
        width: 250,
        render: (_, vehicle) => (
          <div className="crm-cell-stack">
            <Typography.Text strong style={{ fontFamily: 'var(--ge-font-mono)' }}>
              {vehicle.plate}
            </Typography.Text>
            <Typography.Text type="secondary" title={vehicle.id} style={{ fontSize: 12 }}>
              {vehicle.id}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'RFID',
        dataIndex: 'rfidTag',
        key: 'rfidTag',
        width: 190,
        render: (value: string, vehicle) => (
          <div className="crm-cell-stack">
            <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{value}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {vehicle.rfidType}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Marca / modelo',
        key: 'brandModel',
        width: 220,
        render: (_, vehicle) => {
          const brandModel = [vehicle.brand, vehicle.model].filter(Boolean).join(' ');
          return brandModel || <Typography.Text type="secondary">—</Typography.Text>;
        },
      },
      {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        width: 130,
        render: (_, vehicle) => <FleetVehicleStatusBadge status={vehicle.status} />,
      },
      {
        title: 'Autorización RFID',
        dataIndex: 'authorizationStatus',
        key: 'authorizationStatus',
        width: 190,
        render: (_, vehicle) => <FleetAuthorizationBadge status={vehicle.authorizationStatus} />,
      },
      ...(canWrite
        ? [
            {
              title: 'Acciones',
              key: 'actions',
              width: 190,
              align: 'right' as const,
              render: (_: unknown, vehicle: FleetVehicle) => (
                <Space size="small">
                  <Button size="small" onClick={() => onEdit(vehicle)}>
                    Editar
                  </Button>
                  {vehicle.status === 'ACTIVE' ? (
                    <Button size="small" danger onClick={() => onDeactivate(vehicle)}>
                      Inactivar
                    </Button>
                  ) : fleetInactive ? (
                    <Tooltip title={FLEET_INACTIVE_HINT}>
                      <span tabIndex={0}>
                        <Button size="small" disabled>
                          Reactivar
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button size="small" onClick={() => onReactivate(vehicle)}>
                      Reactivar
                    </Button>
                  )}
                </Space>
              ),
            } satisfies ColumnsType<FleetVehicle>[number],
          ]
        : []),
    ],
    [canWrite, fleetInactive, onDeactivate, onEdit, onReactivate],
  );

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar los vehículos"
        description={error}
        action={
          <Button size="small" onClick={onRetry}>
            Reintentar
          </Button>
        }
      />
    );
  }

  return (
    <Table<FleetVehicle>
      rowKey="id"
      size="small"
      loading={loading}
      columns={columns}
      dataSource={vehicles}
      pagination={{
        current: Math.floor(meta.offset / Math.max(meta.limit, 1)) + 1,
        pageSize: meta.limit,
        total: meta.total,
        showSizeChanger: false,
        showTotal: (total) => `${total} vehículos`,
        onChange: (page) => onOffsetChange((page - 1) * meta.limit),
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Esta flota todavía no tiene vehículos."
          />
        ),
      }}
    />
  );
}
